import React, { useState } from 'react';
import { ArrowTopRightOnSquareIcon, Square3Stack3DIcon } from '@heroicons/react/24/outline';
import Button from '@dashboard/components/Button';
import CopyToClipboard from '@dashboard/components/CopyToClipboard';
import Tooltip from '@dashboard/components/Tooltip';
import { Button as ShadcnButton } from '@dashboard/components/ui/button';
import Link from '@dashboard/components/Link';
import removeProtocolFromUrl from '@dashboard/lib/removeProtocolFromUrl';
import getSubdomainCheckoutUrl from '@dashboard/services/getSubdomainCheckoutUrl';
import getCustomDomainCheckoutUrl from '@dashboard/services/getCustomDomainCheckoutUrl';
import { isDevelopment, isPreview as isPreviewEnv } from '@dashboard/services/env';

const HELPER_TEXT_MAP = {
  event: 'Share this link directly with guests',
  checkout: 'Share this link directly with customers',
  form: 'Share this link directly with respondents',
};

const CheckoutShareDirect = ({ page, seller, closeDialog }) => {
  const subdomainCheckoutUrl = getSubdomainCheckoutUrl(page, seller);
  const customDomainCheckoutUrl =
    !isPreviewEnv && !isDevelopment && getCustomDomainCheckoutUrl(page, seller);
  const displayUrl = customDomainCheckoutUrl || subdomainCheckoutUrl;
  const helperText = HELPER_TEXT_MAP[page.type] || HELPER_TEXT_MAP.checkout;

  return (
    <div className="flex flex-col mb-6">
      <div className="flex items-center gap-2 mb-1">
        <Square3Stack3DIcon className="h-5 w-5 text-gray-700 dark:text-muted-foreground" />
        <span className="text-md font-bold">Hosted page</span>
      </div>

      <p className="text-sm text-gray-500 dark:text-muted-foreground mb-3">{helperText}</p>

      <ShareLinkCopyOnly link={displayUrl} />

      <div className="mt-3 flex gap-3 text-sm font-medium">
        <Link
          onClick={closeDialog}
          variant="secondary"
          to={`/pages/${page._id}/customize/settings/url-slug`}
        >
          Change slug
        </Link>

        {customDomainCheckoutUrl && subdomainCheckoutUrl && (
          <Link variant="secondary" to={subdomainCheckoutUrl} blank>
            Open fallback link
          </Link>
        )}

        {!customDomainCheckoutUrl && (
          <Link variant="secondary" to="/settings/domains">
            Add custom domain
          </Link>
        )}
      </div>
    </div>
  );
};

function ShareLinkCopyOnly({ link }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 1000);
  };

  return (
    <div className="flex gap-4 px-4 w-full rounded-lg border border-gray-200 dark:border-border focus:shadow-none bg-gray-50 dark:bg-card min-h-[80px] items-center">
      <a
        href={link}
        target="_blank"
        rel="noopener noreferrer"
        title={link}
        className="min-w-0 flex-1 truncate font-medium text-gray-500 dark:text-muted-foreground hover:text-gray-700 dark:hover:text-muted-foreground text-sm"
      >
        {removeProtocolFromUrl(link)}
      </a>

      <div className="flex shrink-0 items-center gap-2">
        <Tooltip label="Open in a new tab" asChild>
          <ShadcnButton asChild variant="outline" size="icon" aria-label="Open link">
            <a href={link} target="_blank" rel="noopener noreferrer">
              <ArrowTopRightOnSquareIcon className="size-4" />
            </a>
          </ShadcnButton>
        </Tooltip>
        <CopyToClipboard text={link} onCopy={handleCopy}>
          <Button appearance="primary">{copied ? 'Copied' : 'Copy'}</Button>
        </CopyToClipboard>
      </div>
    </div>
  );
}

export default CheckoutShareDirect;
